import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight, Calendar, MapPin, X } from "lucide-react";
import { getEvents } from "@/lib/api";

const STORAGE_KEY = "ss_event_promo_dismissed";
const SUPPRESS_PATHS = ["/events", "/admin", "/payment/success", "/payment/cancel", "/vote-cocktails", "/my-booking"];

export default function EventPromoModal() {
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [open, setOpen] = useState(false);
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (SUPPRESS_PATHS.some((p) => location.pathname.startsWith(p))) return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    } catch { /* ignore */ }

    let cancelled = false;
    let timeoutId;
    getEvents().then((events) => {
      if (cancelled) return;
      const first = events?.[0];
      if (!first) return;
      const sold = Number(first.tickets_sold || 0);
      const total = Number(first.total_tickets || 0);
      if (sold >= total) return; // 0/0 or actually sold-out → suppress
      setEvent(first);
      timeoutId = setTimeout(() => { if (!cancelled) setOpen(true); }, 1400);
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  const dismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  // Focus management + Escape key handling
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); dismiss(); return; }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [open]);

  if (!open || !event) return null;

  const displayDate = event.date_display || (event.date_iso
    ? new Date(event.date_iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : event.date);

  return (
    <div data-testid="event-promo-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.85)" }} onClick={dismiss}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-promo-title"
        onClick={(e) => e.stopPropagation()}
        className="on-dark relative w-full max-w-3xl overflow-hidden border grid md:grid-cols-2 max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--ss-bg-2)", borderColor: "var(--ss-rust)" }}
      >
        <button
          type="button"
          ref={closeBtnRef}
          onClick={dismiss}
          data-testid="event-promo-close"
          aria-label="Close event announcement"
          className="absolute top-3 right-3 z-10 p-2"
          style={{ background: "rgba(15,14,12,0.7)", color: "#F5F1E8" }}
        >
          <X size={18} />
        </button>

        <div className="relative min-h-[220px] md:min-h-[420px]">
          <img src={event.image_url} alt="" aria-hidden="true"
               className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,14,12,0.35), rgba(15,14,12,0.75))" }} />
          <div className="absolute bottom-4 left-4 font-label text-[0.65rem] px-3 py-1"
               style={{ background: "var(--ss-rust)", color: "#F5F1E8", letterSpacing: "2px" }}>
            21+ ONLY
          </div>
        </div>

        <div className="p-6 md:p-8 flex flex-col">
          <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Upcoming · Simply Social</div>
          <h3 id="event-promo-title" className="mt-2 font-display text-3xl md:text-4xl tracking-tight leading-[0.95]">
            {event.headline || event.name}
          </h3>
          <div className="mt-5 space-y-2 font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>
            <div className="inline-flex items-center gap-2"><Calendar size={14} style={{ color: "var(--ss-rust)" }} /> {displayDate}{event.time ? ` · ${event.time}` : ""}</div>
            <div className="inline-flex items-start gap-2"><MapPin size={14} style={{ color: "var(--ss-rust)", marginTop: 3 }} /> <span>{event.location}</span></div>
          </div>
          <p className="mt-5 font-body text-sm leading-[1.7]" style={{ color: "var(--ss-cream-dim)" }}>
            Grab your bestie and come learn how to shake, pour, and create cocktails with DJ Stuntman on the set. Tickets from $65.
          </p>
          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <Link to="/events" onClick={dismiss} data-testid="event-promo-cta" className="btn-primary">
              Get Tickets <ArrowUpRight className="arrow" size={14} />
            </Link>
            <button type="button" onClick={dismiss} data-testid="event-promo-later"
                    className="font-label text-xs" style={{ color: "var(--ss-cream-dim)", letterSpacing: "2px" }}>
              MAYBE LATER
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
